import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle, XCircle, Trophy, AlertTriangle, Loader2, ClipboardList } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function QuizTaker() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const fetchQuiz = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // If user not logged in, redirect to login with return URL
      if (!token) {
        const next = encodeURIComponent(`/quiz/${quizId}`);
        navigate(`/?next=${next}`);
        return;
      }
      const res = await axios.get(`${BACKEND_URL}/api/quizzes/${quizId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuiz(res.data);
      if (res.data?.locked && res.data?.attempt) {
        setResult(res.data.attempt);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || 'Failed to load quiz';
      if (status === 401) {
        const next = encodeURIComponent(`/quiz/${quizId}`);
        navigate(`/?next=${next}`);
        return;
      }
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, [quizId, token, navigate]);

  useEffect(() => {
    fetchQuiz();
  }, [fetchQuiz]);

  const handleSubmit = async () => {
    if (!quiz) return;
    const total = quiz.questions?.length || 0;
    const answered = Object.keys(answers).length;
    if (answered < total) {
      if (!window.confirm(`You've only answered ${answered} of ${total}. Submit anyway?`)) return;
    }
    const answerArr = (quiz.questions || []).map((_, i) =>
      Object.prototype.hasOwnProperty.call(answers, i) ? answers[i] : -1
    );
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/quizzes/${quizId}/submit`,
        { answers: answerArr },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
      toast.success(res.data.passed ? '🎉 You passed!' : 'Submitted — you didn\'t pass this time.');
      // refresh to get locked view with review
      fetchQuiz();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 text-white p-6 flex items-center justify-center">
        <Card className="bg-slate-800 border-slate-700 max-w-md w-full" data-testid="quiz-error-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" /> Can't open this quiz
            </CardTitle>
            <CardDescription className="text-slate-400">{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/')}
              className="bg-slate-700 hover:bg-slate-600 text-white w-full"
            >
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Locked view (already submitted) — show result + review
  if (quiz?.locked && result) {
    return (
      <div className="min-h-screen bg-slate-900 text-white p-3 sm:p-4 md:p-6" data-testid="quiz-result-view">
        <div className="max-w-3xl mx-auto space-y-3 sm:space-y-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/')}
            className="border-slate-600 bg-slate-800 hover:bg-slate-700 text-white"
            data-testid="quiz-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Card className={`border-2 ${result.passed ? 'border-emerald-600 bg-emerald-900/20' : 'border-red-600 bg-red-900/20'}`}>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-3">
                {result.passed ? (
                  <Trophy className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-400 shrink-0" />
                ) : (
                  <XCircle className="w-8 h-8 sm:w-10 sm:h-10 text-red-400 shrink-0" />
                )}
                <div className="min-w-0">
                  <h2 className="text-xl sm:text-2xl font-bold break-words">{quiz.title}</h2>
                  <p className="text-xs sm:text-sm text-slate-300">
                    {result.score} / {result.total} correct · {result.percentage}% ·{' '}
                    {result.passed ? 'PASSED' : 'NOT PASSED'} (pass {quiz.passing_pct}%)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-base">Review your answers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 p-4 sm:p-6 pt-0">
              {(result.review || quiz.attempt?.review || []).map((r, i) => {
                const chosen = r.chosen_index;
                const correct = r.correct_index;
                const isRight = chosen === correct;
                return (
                  <div
                    key={i}
                    className={`p-3 rounded border ${
                      isRight
                        ? 'border-emerald-700 bg-emerald-900/20'
                        : 'border-red-700 bg-red-900/20'
                    }`}
                    data-testid={`quiz-review-${i}`}
                  >
                    <div className="flex items-start gap-2">
                      {isRight ? (
                        <CheckCircle className="w-4 h-4 text-emerald-400 mt-1 shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-400 mt-1 shrink-0" />
                      )}
                      <div className="flex-1">
                        <div className="font-medium text-white text-sm">
                          Q{i + 1}. {r.text}
                        </div>
                        <div className="space-y-1 mt-2">
                          {(r.options || []).map((opt, oi) => (
                            <div
                              key={oi}
                              className={`text-xs px-2 py-1 rounded ${
                                oi === correct
                                  ? 'bg-emerald-700/40 text-emerald-200 font-medium'
                                  : oi === chosen
                                  ? 'bg-red-700/40 text-red-200'
                                  : 'text-slate-400'
                              }`}
                            >
                              {String.fromCharCode(65 + oi)}. {opt}
                              {oi === correct && ' ✓'}
                              {oi === chosen && !isRight && ' ← your answer'}
                            </div>
                          ))}
                        </div>
                        {r.rationale && (
                          <div className="text-xs text-slate-400 mt-2 italic">{r.rationale}</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Taker view (not yet submitted)
  return (
    <div className="min-h-screen bg-slate-900 text-white p-3 sm:p-4 md:p-6" data-testid="quiz-taker-view">
      <div className="max-w-3xl mx-auto space-y-3 sm:space-y-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/')}
          className="border-slate-600 bg-slate-800 hover:bg-slate-700 text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <ClipboardList className="w-5 h-5 text-blue-400 shrink-0" />
              <span className="break-words">{quiz?.title}</span>
            </CardTitle>
            <CardDescription className="text-slate-400 text-sm">
              {quiz?.description}
              <div className="flex items-center gap-2 mt-2 text-xs flex-wrap">
                <Badge variant="outline" className="border-slate-500 text-slate-300">
                  {quiz?.question_count} questions
                </Badge>
                <Badge variant="outline" className="border-slate-500 text-slate-300">
                  Pass mark {quiz?.passing_pct}%
                </Badge>
                {quiz?.deadline && (
                  <Badge variant="outline" className="border-amber-500 text-amber-300">
                    Deadline {quiz.deadline}
                  </Badge>
                )}
              </div>
            </CardDescription>
          </CardHeader>
        </Card>

        {(quiz?.questions || []).map((q, idx) => (
          <Card key={idx} className="bg-slate-800 border-slate-700" data-testid={`quiz-take-q-${idx}`}>
            <CardContent className="p-3 sm:p-4">
              <div className="font-medium text-white mb-3 text-sm sm:text-base">
                Q{idx + 1}. {q.text}
              </div>
              <div className="space-y-2">
                {(q.options || []).map((opt, oi) => (
                  <label
                    key={oi}
                    className={`flex items-center gap-3 p-3 sm:p-2 rounded border cursor-pointer transition-colors min-h-[44px] ${
                      answers[idx] === oi
                        ? 'border-blue-500 bg-blue-900/30'
                        : 'border-slate-600 bg-slate-700/30 hover:bg-slate-700/60'
                    }`}
                    data-testid={`quiz-take-q-${idx}-opt-${oi}`}
                  >
                    <input
                      type="radio"
                      name={`q-${idx}`}
                      checked={answers[idx] === oi}
                      onChange={() => setAnswers({ ...answers, [idx]: oi })}
                      className="accent-blue-500 w-4 h-4 shrink-0"
                    />
                    <span className="text-sm text-white break-words">
                      <span className="text-slate-400 mr-2">{String.fromCharCode(65 + oi)}.</span>
                      {opt}
                    </span>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        <div className="sticky bottom-3 sm:bottom-4 z-10">
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 text-base shadow-lg shadow-emerald-900/40"
            data-testid="quiz-submit-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
            ) : (
              <>Submit quiz</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default QuizTaker;
